import React, { Component } from 'react';
import {
  ActivityIndicator, ScrollView, StyleSheet, Text, View,
} from 'react-native';

import { AreaCategory, UserProfile } from 'fitbeat.gym-core';
import {
  GymTimeZone, TimeSlotEligibility, TimeSlotType,
  WorkoutType,
} from 'fitbeat.models';
import { connect } from 'react-redux';
import Slot from './ZoneSlot';

import { TranslateProp } from '../../common-types';
import { HideScrollBarOnWeb } from '../../components/HideScrollBarOnWeb';
import Theme from '../../theme';
import { LinearGradient } from '../../ui';
import { defaultScale } from '../../utils/scaling';
import { ITimeSlotUIViewModel } from '../reducers';
import { IAppStore } from '../../HomeWorkouts';
import { classTimesSelector } from '../selectors';

interface IProps {
  t: TranslateProp
  userProfile: UserProfile
  gymTimezone: GymTimeZone
  showHeader: boolean
  pendingSlot: any
  isLoadingTimeSlots: boolean
  classTitle: string
  classDuration: WorkoutType
  preferredStartTime: number
  areaCategory: AreaCategory,
  setPendingSlot: (pendingSlot: any, areaCategory: AreaCategory | undefined) => void
  setTimeSlotType: (timeSlotType: TimeSlotType) => void
  setTimeSlotEligibility: (eligibility: TimeSlotEligibility) => void
}

interface IMapStateToProps {
  classTimes: ITimeSlotUIViewModel[]
}

export const slotDimensions = {
  width: defaultScale(105),
  marginRight: defaultScale(10),
};

class TimeSlots extends Component<IProps & IMapStateToProps> {
  private timeslotScroller: ScrollView | null;

  constructor(props: IProps & IMapStateToProps) {
    super(props);

    this.timeslotScroller = null;
  }

  public scrollToTimeSlot = (itemIndex, shouldAnimate = false) => {
    const distance = slotDimensions.width + slotDimensions.marginRight;

    if (this.timeslotScroller) {
      const distanceToScroll = Math.max((itemIndex - 1) * distance, 0);
      this.timeslotScroller.scrollTo({ x: distanceToScroll, animated: shouldAnimate });
    }
  };

  public renderSessionBlocks = (timeSlots) => {
    const {
      t,
      setPendingSlot,
      setTimeSlotType,
      setTimeSlotEligibility,
      gymTimezone,
      preferredStartTime,
      areaCategory,
    } = this.props;

    const preferredSlot = timeSlots.reduce((closestSlot, currentSlot) => {
      const currentDiff = Math.abs(currentSlot.startTime - preferredStartTime);
      const closestDiff = Math.abs(closestSlot.startTime - preferredStartTime);

      return currentDiff < closestDiff ? currentSlot : closestSlot;
    });

    return timeSlots
      .map((slot, index) => {
        const key = `${slot.startTime}${slot.endTime}`;
        const isLast = index === timeSlots.length - 1;
        const isPreferred = slot === preferredSlot;

        return (
          <Slot
            t={t}
            key={key}
            slot={slot}
            index={index}
            isLast={isLast}
            isSelected={slot.isSelected}
            setPendingSlot={setPendingSlot}
            scrollToTimeSlot={this.scrollToTimeSlot}
            setTimeSlotEligibility={setTimeSlotEligibility}
            setTimeSlotType={setTimeSlotType}
            gymTimezone={gymTimezone}
            isPreferred={isPreferred}
            areaCategory={areaCategory}
          />
        );
      });
  };

  public renderScrollGradient = (side: 'left' | 'right') => {
    const styleClass = side === 'left' ? styles.leftGradient : styles.rightGradient;
    const startX = side === 'left' ? 1 : 0;
    const endX = side === 'left' ? 0 : 1;

    return (
      <LinearGradient
        colors={['rgba(255, 255, 255, 0)', `${Theme.WHITE}`]}
        style={[styles.gradientScrollIndicator, styleClass]}
        start={{ x: startX, y: 0 }}
        end={{ x: endX, y: 0 }}
      />
    );
  };

  public render() {
    const {
      t,
      isLoadingTimeSlots,
      classTitle,
      classTimes,
    } = this.props;

    if (isLoadingTimeSlots) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            size='small'
            color={Theme.PRIMARY}
            animating={isLoadingTimeSlots}
          />
        </View>
      );
    }

    return (
      <View style={{ flex: 1 }}>
        <ScrollView>
          <View>
            <View style={styles.container}>
              <Text style={styles.classTimeText}>
                {classTitle}
              </Text>
              <View style={styles.scrollContainer}>
                {this.renderScrollGradient('left')}
                {this.renderScrollGradient('right')}
                <HideScrollBarOnWeb>
                  <ScrollView
                    style={styles.timeslotsContainer}
                    horizontal
                    scrollToOverflowEnabled
                    showsHorizontalScrollIndicator={false}
                    ref={(scroller) => this.timeslotScroller = scroller}
                  >
                    {classTimes.length > 0
                      ? this.renderSessionBlocks(classTimes)
                      : <Text style={styles.noAvailabilityText}>{t('noAvailabilityToday')}</Text>}
                  </ScrollView>
                </HideScrollBarOnWeb>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  container: {
    flexDirection: 'column',
  },
  scrollContainer: {
    position: 'relative',
  },
  timeslotsContainer: {
    paddingLeft: defaultScale(15),
  },
  gradientScrollIndicator: {
    height: '100%',
    position: 'absolute',
    top: 0,
    zIndex: 1,
  },
  leftGradient: {
    left: 0,
    width: defaultScale(20),
  },
  rightGradient: {
    right: 0,
    width: defaultScale(40),
  },
  classTimeText: {
    ...Theme.textStyle.bodyCopyBold,
    marginVertical: defaultScale(10),
    paddingLeft: defaultScale(15),
  },
  noAvailabilityText: {
    opacity: 0.25,
  },
});

const mapStateToProps = (state: IAppStore, ownProps: IProps): IMapStateToProps => ({
  classTimes: classTimesSelector(state, ownProps.areaCategory, ownProps.classDuration),
});

export default connect(mapStateToProps)(TimeSlots);
